"use client";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import Navbar from "@/components/navigation/Navbar";
import BlurStack, { type BlurLayer } from "@/components/ui/blur-stack";
import LazyMotionProvider from "@/features/landing/components/LazyMotionProvider";
import { usePathname } from "@/i18n/navigation";
import LandingProvidersLayout from "@/layouts/LandingProvidersLayout";

// Footer is always below the fold on landing pages — defer it so it doesn't
// compete with hero-above-the-fold rendering for main-thread time.
const Footer = dynamic(() => import("@/components/navigation/Footer"), {
  ssr: true,
  loading: () => null,
});

const BLUR_CONFIG: BlurLayer[] = [
  { blur: 0.078125, maskStops: [0, 12.5, 25, 37.5], zIndex: 1 },
  { blur: 0.15625, maskStops: [12.5, 25, 37.5, 50], zIndex: 2 },
  { blur: 0.3125, maskStops: [25, 37.5, 50, 62.5], zIndex: 3 },
  { blur: 0.625, maskStops: [37.5, 50, 62.5, 75], zIndex: 4 },
  { blur: 1.25, maskStops: [50, 62.5, 75, 87.5], zIndex: 5 },
  { blur: 2.5, maskStops: [62.5, 75, 87.5, 100], zIndex: 6 },
  { blur: 5, maskStops: [75, 87.5, 100, 100], zIndex: 7 },
  { blur: 10, maskStops: [87.5, 100, 100, 100], zIndex: 8 },
];

export default function LandingLayoutShell({
  children,
}: {
  children: ReactNode;
}) {
  const pathname = usePathname();
  const isDesktopLogin = pathname === "/desktop-login";

  return (
    <LandingProvidersLayout>
      <div className="relative ">
        <div
          id="navbar-backdrop"
          className="pointer-events-none fixed inset-0 z-40 bg-black/20 opacity-0 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
        />

        <BlurStack
          className="fixed h-[100px] w-screen z-1000 bottom-0 pointer-events-none"
          config={BLUR_CONFIG}
        />

        <LazyMotionProvider>
          {!isDesktopLogin && <Navbar />}

          <main id="main-content" className="min-h-screen">
            {children}
          </main>
        </LazyMotionProvider>

        {!isDesktopLogin && (
          <div className="flex w-full flex-row items-center justify-center gap-3 px-4 py-6">
            <Link
              href="https://startupfa.me/s/gaia?utm_source=heygaia.io"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src="https://startupfa.me/badges/featured/dark-small-rounded.webp"
                alt="GAIA - Featured on Startup Fame"
                width={240}
                height={37}
                unoptimized
              />
            </Link>
            <Link
              href="https://www.producthunt.com/products/heygaia/launches/gaia-v0?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-gaia-v0"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1000528&theme=dark&t=1782806818421"
                alt="GAIA v0 - Your Personal AI assistant for email, calendar, tasks & more | Product Hunt"
                width={250}
                height={54}
                unoptimized
              />
            </Link>
          </div>
        )}

        {!isDesktopLogin && <Footer />}
      </div>
    </LandingProvidersLayout>
  );
}
